'use client'

import React from 'react'
import {
  Layers,
  Database,
  FileText,
  Globe,
  Link2,
  Search,
  Brain,
  Cpu,
  MessageSquare,
  TrendingUp,
  Users,
  UserPlus,
  Share2,
  ArrowDown,
  AlertTriangle,
  Activity,
  Zap,
} from 'lucide-react'

/**
 * LLM Visibility Diagrams
 * Visual components for the knowledge graph + LLM visibility blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * LLM Visibility Stack - 5-layer framework
 */
export function LLMVisibilityStack() {
  const layers = [
    {
      label: 'Multi-Source Authority',
      description:
        'Cross-site entity linking, sameAs connections, consistent signals across domains',
      icon: Globe,
      color: '#22c55e',
      layer: 5,
    },
    {
      label: 'Machine-Readable Context',
      description:
        'llms.txt, robots.txt AI directives, FAQ schema, structured data signals',
      icon: Cpu,
      color: '#F90B8A',
      layer: 4,
    },
    {
      label: 'Content Signals',
      description:
        'Authoritative citations, statistical claims, quotable passages, topical depth',
      icon: FileText,
      color: '#FDC304',
      layer: 3,
    },
    {
      label: 'Knowledge Graph',
      description:
        'Entity definitions, @graph enrichment, topic clusters, relationship mapping',
      icon: Share2,
      color: '#8b5cf6',
      layer: 2,
    },
    {
      label: 'Entity Foundation',
      description:
        'DefinedTerm schemas, Organization identity, Person profiles, sameAs URIs',
      icon: Database,
      color: '#008dcc',
      layer: 1,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE LLM VISIBILITY STACK
        </h3>
        <p className='text-sm text-muted-foreground'>
          Five layers that determine whether AI search engines cite your content
        </p>
      </div>

      <div className='flex flex-col gap-2'>
        {layers.map((layer, i) => (
          <React.Fragment key={layer.label}>
            <div className='p-4 rounded-lg bg-background border border-border/60'>
              <div className='flex items-start gap-3'>
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0'
                  style={{ backgroundColor: `${layer.color}15` }}
                >
                  <layer.icon
                    className='w-5 h-5'
                    style={{ color: layer.color }}
                  />
                </div>
                <div className='flex-1'>
                  <div className='flex items-center gap-2 mb-1'>
                    <span
                      className='text-xs font-bold px-2 py-0.5 rounded-full'
                      style={{
                        backgroundColor: `${layer.color}15`,
                        color: layer.color,
                      }}
                    >
                      L{layer.layer}
                    </span>
                    <span className='text-sm font-semibold text-foreground'>
                      {layer.label}
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    {layer.description}
                  </p>
                </div>
              </div>
            </div>
            {i < layers.length - 1 && (
              <div className='flex justify-center'>
                <ArrowDown className='w-4 h-4 text-muted-foreground' />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className='mt-6 p-4 rounded-lg bg-[#8b5cf6]/5 border border-[#8b5cf6]/20'>
        <div className='flex items-start gap-3'>
          <Layers className='w-5 h-5 text-[#8b5cf6] flex-shrink-0 mt-0.5' />
          <div>
            <p className='text-sm font-semibold text-foreground'>
              Each layer amplifies the ones below it.
            </p>
            <p className='text-xs text-muted-foreground mt-1'>
              Most teams jump to Layer 4 (llms.txt) without Layers 1-2. That is
              like building a house starting from the roof. The entity
              foundation and knowledge graph are what make everything else
              meaningful to LLMs.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Entity Linking Diagram - Two domains with shared entities
 */
export function EntityLinkingDiagram() {
  const domains = [
    {
      name: 'pixelmojo.io',
      color: '#008dcc',
      entities: [
        'Organization',
        'Services (6)',
        'Products (2)',
        'Methodologies (3)',
      ],
    },
    {
      name: 'lloydpilapil.com',
      color: '#8b5cf6',
      entities: ['Person', 'KnowsAbout (7)', 'WorksFor', 'SameAs URIs'],
    },
  ]

  const sharedEntities = [
    { name: 'Thread-Based Engineering', type: 'DefinedTerm' },
    { name: 'AX Design', type: 'DefinedTerm' },
    { name: 'GEO', type: 'DefinedTerm' },
    { name: 'Lakbay AI', type: 'SoftwareApplication' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          CROSS-SITE ENTITY LINKING
        </h3>
        <p className='text-sm text-muted-foreground'>
          Two domains, one knowledge graph, connected via sameAs and shared @id
          URIs
        </p>
      </div>

      <div className='grid md:grid-cols-2 gap-4 mb-4'>
        {domains.map(domain => (
          <div
            key={domain.name}
            className='p-4 rounded-lg bg-background border border-border/60'
          >
            <div className='text-center mb-3'>
              <span
                className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider'
                style={{
                  backgroundColor: `${domain.color}10`,
                  border: `1px solid ${domain.color}30`,
                  color: domain.color,
                }}
              >
                <Globe className='w-3 h-3' />
                {domain.name}
              </span>
            </div>
            <div className='space-y-1.5'>
              {domain.entities.map(entity => (
                <div
                  key={entity}
                  className='text-xs text-foreground/80 px-3 py-1.5 bg-muted rounded-md'
                >
                  {entity}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Connection Bridge */}
      <div className='flex items-center justify-center gap-2 my-4'>
        <div className='h-px flex-1 bg-[var(--border)]' />
        <span className='text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 flex items-center gap-1'>
          <Link2 className='w-3 h-3' />
          Shared Entities
        </span>
        <div className='h-px flex-1 bg-[var(--border)]' />
      </div>

      <div className='grid grid-cols-2 gap-2'>
        {sharedEntities.map(entity => (
          <div
            key={entity.name}
            className='p-3 rounded-lg bg-[#FDC304]/5 border border-[#FDC304]/20 text-center'
          >
            <p className='text-xs font-semibold text-foreground'>
              {entity.name}
            </p>
            <p className='text-[10px] text-muted-foreground mt-0.5'>
              @type: {entity.type}
            </p>
          </div>
        ))}
      </div>

      <div className='mt-4 p-3 rounded-lg bg-[#008dcc]/5 border border-[#008dcc]/20'>
        <p className='text-xs text-center text-muted-foreground'>
          <strong className='text-foreground'>
            The sameAs bridge is bidirectional.
          </strong>{' '}
          When an LLM encounters &quot;Lloyd Pilapil&quot; on either site,
          JSON-LD connects it to the same Person entity. The organization,
          methodologies, and products all resolve to the same @id URIs
          regardless of which domain the LLM indexed first.
        </p>
      </div>
    </div>
  )
}

/**
 * Before/After Metrics - Real analytics from GA4
 */
export function BeforeAfterMetrics() {
  const metrics = [
    {
      label: 'Active Users',
      value: '248',
      change: '+18.1%',
      positive: true,
      icon: Users,
      color: '#008dcc',
      detail: 'Last 7 days vs previous period',
    },
    {
      label: 'New Users',
      value: '229',
      change: '+21.8%',
      positive: true,
      icon: UserPlus,
      color: '#8b5cf6',
      detail: 'Last 7 days vs previous period',
    },
    {
      label: 'Referral Traffic',
      value: '6',
      change: 'Above forecast',
      positive: true,
      icon: TrendingUp,
      color: '#22c55e',
      detail: 'GA4 predicted 1-5 users; actual exceeded range',
    },
    {
      label: 'Key Events (Direct)',
      value: '19',
      change: '3x predicted',
      positive: true,
      icon: Zap,
      color: '#FDC304',
      detail: 'GA4 predicted 0-6 events; actual was 19',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          WHAT THE DATA SHOWS
        </h3>
        <p className='text-sm text-muted-foreground'>
          GA4 analytics for pixelmojo.io, 7 days after deploying the knowledge
          graph
        </p>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        {metrics.map(metric => (
          <div
            key={metric.label}
            className='p-4 rounded-lg bg-background border border-border/60'
          >
            <div className='flex items-center gap-2 mb-2'>
              <div
                className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'
                style={{ backgroundColor: `${metric.color}15` }}
              >
                <metric.icon
                  className='w-4 h-4'
                  style={{ color: metric.color }}
                />
              </div>
              <span className='text-xs font-medium text-muted-foreground'>
                {metric.label}
              </span>
            </div>
            <div className='flex items-baseline gap-2'>
              <span className='text-2xl font-bold text-foreground'>
                {metric.value}
              </span>
              <span
                className='text-xs font-semibold'
                style={{ color: metric.color }}
              >
                {metric.change}
              </span>
            </div>
            <p className='text-[10px] text-muted-foreground mt-1'>
              {metric.detail}
            </p>
          </div>
        ))}
      </div>

      <div className='mt-4 p-3 rounded-lg bg-[#FDC304]/5 border border-[#FDC304]/20'>
        <div className='flex items-start gap-2'>
          <AlertTriangle className='w-4 h-4 text-[#FDC304] flex-shrink-0 mt-0.5' />
          <p className='text-xs text-muted-foreground'>
            <strong className='text-foreground'>Honest caveat:</strong> These
            numbers correlate with deploying the knowledge graph, but
            correlation is not causation. We also published new content and made
            technical SEO improvements during the same period. The referral and
            key event anomalies specifically align with the knowledge graph
            deployment timeline, which is why we highlight them separately.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * RAG Pipeline Diagram - How LLMs discover and cite sources
 */
export function RAGPipelineDiagram() {
  const steps = [
    {
      label: 'User Query',
      sublabel: '"What agency does AI product development?"',
      icon: MessageSquare,
      color: '#008dcc',
    },
    {
      label: 'Retrieval',
      sublabel: 'LLM searches its index for relevant documents',
      icon: Search,
      color: '#8b5cf6',
    },
    {
      label: 'Entity Resolution',
      sublabel: 'Matches structured data to resolve who/what entities are',
      icon: Database,
      color: '#FDC304',
      highlight: true,
    },
    {
      label: 'Context Assembly',
      sublabel: 'Ranks and combines sources by authority signals',
      icon: Brain,
      color: '#F90B8A',
      highlight: true,
    },
    {
      label: 'Response Generation',
      sublabel: 'Synthesizes answer with citations from top-ranked sources',
      icon: FileText,
      color: '#22c55e',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          HOW LLMS DISCOVER SOURCES
        </h3>
        <p className='text-sm text-muted-foreground'>
          Simplified RAG pipeline showing where structured data matters most
        </p>
      </div>

      <div className='flex flex-col gap-2'>
        {steps.map((step, i) => (
          <React.Fragment key={step.label}>
            <div
              className={`p-3 rounded-lg border ${
                step.highlight
                  ? 'bg-[#FDC304]/5 border-[#FDC304]/30'
                  : 'bg-background border-border/60'
              }`}
            >
              <div className='flex items-center gap-3'>
                <div
                  className='w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0'
                  style={{ backgroundColor: `${step.color}15` }}
                >
                  <step.icon
                    className='w-4 h-4'
                    style={{ color: step.color }}
                  />
                </div>
                <div className='flex-1'>
                  <div className='flex items-center gap-2'>
                    <p className='text-sm font-semibold text-foreground leading-tight'>
                      {step.label}
                    </p>
                    {step.highlight && (
                      <span className='text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#FDC304]/15 text-[#FDC304]'>
                        YOUR DATA MATTERS HERE
                      </span>
                    )}
                  </div>
                  <p className='text-xs text-muted-foreground'>
                    {step.sublabel}
                  </p>
                </div>
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className='flex justify-center'>
                <ArrowDown className='w-4 h-4 text-muted-foreground' />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className='mt-6 p-4 rounded-lg bg-[#F90B8A]/5 border border-[#F90B8A]/20'>
        <div className='flex items-start gap-3'>
          <Activity className='w-5 h-5 text-[#F90B8A] flex-shrink-0 mt-0.5' />
          <div>
            <p className='text-sm font-semibold text-foreground'>
              Steps 3 and 4 are where knowledge graphs win.
            </p>
            <p className='text-xs text-muted-foreground mt-1'>
              Without structured entity data, the LLM treats your site like
              every other page. With it, you give the model something concrete
              to resolve against: named entities, defined relationships, and
              explicit authority signals. This is the difference between
              &quot;possibly relevant&quot; and &quot;authoritative
              source.&quot;
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
